import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { leadsApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Phone, 
  Mail, 
  GraduationCap, 
  Target, 
  Calendar,
  Trash2,
  Loader2,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

// Status configuration
const STATUSES = [
  { id: 'new', label: 'Nouveau', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Users },
  { id: 'contacted', label: 'Contacté', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Phone },
  { id: 'converted', label: 'Converti', color: 'bg-green-100 text-green-800 border-green-200', icon: GraduationCap },
  { id: 'lost', label: 'Perdu', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Target },
];

// Sortable Lead Card Component
const SortableLeadCard = ({ lead, onDelete, isDeleting }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getLevelLabel = (level) => {
    const levels = {
      debutant: 'Débutant',
      A1: 'A1',
      A2: 'A2',
      B1: 'B1',
      B2: 'B2',
      C1: 'C1',
    };
    return levels[level] || level;
  };

  const getObjectiveLabel = (objective) => {
    const objectives = {
      studies: 'Études',
      professional: 'Formation pro',
      work: 'Travail',
      family: 'Famille',
      other: 'Autre',
    };
    return objectives[objective] || objective;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-foreground truncate">{lead.name}</h4>
          <p className="text-sm text-muted-foreground truncate">{lead.email}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(lead.id);
          }}
          disabled={isDeleting}
          className="text-red-500 hover:text-red-700 hover:bg-red-50 -mt-1 -mr-2"
          data-testid={`delete-lead-${lead.id}`}
        >
          {isDeleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </Button>
      </div>

      {lead.whatsapp && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Phone className="w-3 h-3" />
          <span>{lead.whatsapp}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        {lead.german_level && (
          <Badge variant="outline" className="text-xs">
            <GraduationCap className="w-3 h-3 mr-1" />
            {getLevelLabel(lead.german_level)}
          </Badge>
        )}
        {lead.objective && (
          <Badge variant="outline" className="text-xs">
            <Target className="w-3 h-3 mr-1" />
            {getObjectiveLabel(lead.objective)}
          </Badge>
        )}
      </div>

      {lead.message && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-3 h-3 mt-0.5 text-muted-foreground flex-shrink-0" />
            <p className="text-xs text-muted-foreground line-clamp-2">{lead.message}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
        <Calendar className="w-3 h-3" />
        <span>{new Date(lead.created_at).toLocaleDateString('fr-FR')}</span>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ status, leads, onDelete, deletingId }) => {
  const StatusIcon = status.icon;
  
  return (
    <div className="flex-1 min-w-[280px] max-w-[350px]">
      <div className={`rounded-xl border ${status.color} p-4`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <StatusIcon className="w-5 h-5" />
            <h3 className="font-heading font-semibold">{status.label}</h3>
          </div>
          <Badge variant="secondary" className="text-xs">
            {leads.length}
          </Badge>
        </div>

        <SortableContext items={leads.map(l => l.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3 min-h-[200px]">
            {leads.map((lead) => (
              <SortableLeadCard
                key={lead.id}
                lead={lead}
                onDelete={onDelete}
                isDeleting={deletingId === lead.id}
              />
            ))}
            {leads.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Aucun lead
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

// Main LeadsPage Component
const LeadsPage = () => {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch leads
  const { data: leads = [], isLoading, refetch } = useQuery({
    queryKey: ['leads'],
    queryFn: () => leadsApi.getAll(),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ leadId, status }) => leadsApi.updateStatus(leadId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      queryClient.invalidateQueries(['leads']);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (leadId) => leadsApi.delete(leadId),
    onSuccess: () => {
      toast.success('Lead supprimé');
      queryClient.invalidateQueries(['leads']);
      setDeletingId(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      setDeletingId(null);
    },
  });

  const handleDelete = (leadId) => {
    setDeletingId(leadId);
    deleteMutation.mutate(leadId);
  };

  // Group leads by status
  const leadsByStatus = STATUSES.reduce((acc, status) => {
    acc[status.id] = leads.filter(lead => lead.status === status.id);
    return acc;
  }, {});

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeLead = leads.find(l => l.id === active.id);
    if (!activeLead) return;

    // Find which column it was dropped on
    let newStatus = null;
    
    // Check if dropped on another lead card
    const overLead = leads.find(l => l.id === over.id);
    if (overLead) {
      newStatus = overLead.status;
    }

    // If status changed, update it
    if (newStatus && newStatus !== activeLead.status) {
      updateStatusMutation.mutate({ leadId: activeLead.id, status: newStatus });
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over) return;
    
    const activeLead = leads.find(l => l.id === active.id);
    const overLead = leads.find(l => l.id === over.id);
    
    if (!activeLead || !overLead) return;
    if (activeLead.status === overLead.status) return;
    
    // Update locally for immediate feedback
    // The actual API call happens on dragEnd
  };

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            Gestion des Leads
          </h1>
          <p className="text-muted-foreground">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} au total
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          data-testid="refresh-leads-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATUSES.map((status) => {
          const StatusIcon = status.icon;
          const count = leadsByStatus[status.id]?.length || 0;
          return (
            <Card key={status.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.color}`}>
                    <StatusIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground">{status.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUSES.map((status) => (
            <KanbanColumn
              key={status.id}
              status={status}
              leads={leadsByStatus[status.id] || []}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          ))}
        </div>

        <DragOverlay>
          {activeLead ? (
            <div className="bg-white rounded-lg border border-primary shadow-lg p-4 w-[280px] opacity-90">
              <h4 className="font-medium text-foreground">{activeLead.name}</h4>
              <p className="text-sm text-muted-foreground">{activeLead.email}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Empty State */}
      {leads.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">Aucun lead</h3>
            <p className="text-muted-foreground">
              Les leads apparaîtront ici lorsqu'ils s'inscriront via le formulaire.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadsPage;
